/**
 * tallyNoSchedule.ts
 *
 * Queries the Make.com webhook that checks whether a psychologist has already
 * submitted the "no agendamiento" Tally form (nPX6J1) for a given set of
 * patients since a reference date.
 *
 * The webhook (Scenario B in Make) queries a Data Store that is kept in sync
 * by Scenario A, which fires every time a new Tally response is submitted.
 * No Tally API key is required — authentication lives entirely in Make.
 *
 * Expected request  → POST { psychologist_email: string, since_date: string (ISO) }
 * Expected response ← [{ key: string, process_id: string, reason: string, submitted_at: string }, ...]
 *
 * Results are cached in sessionStorage for 5 minutes to avoid redundant
 * round-trips to Make on every modal open.
 */

type MakeResponseItem = {
  key: string;
  process_id: string;
  reason?: string;
  submitted_at?: string;
};

export type NoScheduleEntry = {
  reason: string;
  /** ISO string of when the Tally form was submitted */
  submittedAt: string;
};

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

function getCacheKey(email: string): string {
  return `tally-no-schedule:${email}`;
}

function readCache(email: string): Map<string, NoScheduleEntry> | null {
  try {
    const raw = sessionStorage.getItem(getCacheKey(email));
    if (!raw) return null;
    const { ts, entries } = JSON.parse(raw) as { ts: number; entries: [string, NoScheduleEntry][] };
    if (Date.now() - ts > CACHE_TTL_MS) return null;
    return new Map(entries);
  } catch {
    return null;
  }
}

function writeCache(email: string, map: Map<string, NoScheduleEntry>): void {
  try {
    sessionStorage.setItem(
      getCacheKey(email),
      JSON.stringify({ ts: Date.now(), entries: Array.from(map.entries()) }),
    );
  } catch {
    // sessionStorage full or unavailable — skip silently
  }
}

export function invalidateNoScheduleCache(email: string): void {
  try { sessionStorage.removeItem(getCacheKey(email)); } catch { /* noop */ }
}

/**
 * Returns a Map of processId → { reason, submittedAt } for all patients where
 * this psychologist already submitted the no-agendamiento form.
 *
 * The caller should compare submittedAt against the patient's lastSessionDate
 * to determine whether the submission is still valid (i.e. it occurred after
 * the last session, meaning the situation hasn't changed since).
 *
 * Returns an empty Map on any error so the caller can fail gracefully.
 * Results are cached in sessionStorage for 5 minutes.
 */
export async function fetchNoScheduleMap(
  psychologistEmail: string,
  sinceISO: string,
): Promise<Map<string, NoScheduleEntry>> {
  const cached = readCache(psychologistEmail);
  if (cached) return cached;

  const webhookUrl = process.env.REACT_APP_MAKE_NO_AGENDAMIENTO_WEBHOOK;
  if (!webhookUrl) return new Map();

  try {
    const res = await fetch(webhookUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ psychologist_email: psychologistEmail, since_date: sinceISO }),
    });

    if (!res.ok) return new Map();

    const data: MakeResponseItem[] = await res.json();
    if (!Array.isArray(data)) return new Map();

    // Scenario B returns ALL DataStore records — filter client-side by key prefix.
    const prefix = psychologistEmail;
    const result = new Map(
      data
        .filter((item): item is MakeResponseItem & { process_id: string } =>
          Boolean(item.process_id) &&
          Boolean(item.key) &&
          (item.key.startsWith(`${prefix}|`) || item.key.startsWith(prefix)),
        )
        .map((item): [string, NoScheduleEntry] => [
          item.process_id,
          {
            reason: item.reason ?? '',
            submittedAt: item.submitted_at ?? '',
          },
        ]),
    );

    writeCache(psychologistEmail, result);
    return result;
  } catch {
    return new Map();
  }
}
